import { ConvexProviderWithAuthKit } from '@convex-dev/workos';
import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';
import { AuthKitProvider, useAuth } from '@workos-inc/authkit-react';
import { ConvexReactClient } from 'convex/react';
import type { ReactNode } from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './app.css';
import { AppErrorFallback } from './components/AppErrorFallback';
import { ConfigurationError } from './components/ConfigurationError';
import { ConfirmProvider } from './components/ui/ConfirmProvider';
import { PostHogAuthBridge, posthogClient } from './lib/posthog';

const convexUrl = import.meta.env.VITE_CONVEX_URL;
const workosClientId = import.meta.env.VITE_WORKOS_CLIENT_ID || __WORKOS_CLIENT_ID__;
const workosRedirectUri = import.meta.env.VITE_WORKOS_REDIRECT_URI || __WORKOS_REDIRECT_URI__;
const workosApiHostname = import.meta.env.VITE_WORKOS_API_HOSTNAME;
const workosDevMode = resolveWorkOSDevMode(import.meta.env.VITE_WORKOS_DEV_MODE, workosApiHostname);

function isLocalDevelopmentHost(hostname: string) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
}

function parseBooleanEnv(value: string | undefined): boolean | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') return true;
  if (normalized === 'false' || normalized === '0') return false;
  return undefined;
}

function resolveWorkOSDevMode(rawValue: string | undefined, apiHostname: string | undefined): boolean {
  const configured = parseBooleanEnv(rawValue);
  if (configured !== undefined) {
    return configured;
  }
  // WorkOS recommends devMode when no custom auth domain is configured.
  if (!apiHostname) {
    return true;
  }
  return isLocalDevelopmentHost(window.location.hostname);
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

if (!convexUrl) {
  createRoot(rootElement).render(
    <ConfigurationError>`VITE_CONVEX_URL` fehlt in Ihrer Umgebungsdatei.</ConfigurationError>,
  );
} else if (!workosClientId || !workosRedirectUri) {
  createRoot(rootElement).render(
    <ConfigurationError>
      `VITE_WORKOS_CLIENT_ID`/`VITE_WORKOS_REDIRECT_URI` (oder als Fallback `WORKOS_CLIENT_ID`/`WORKOS_REDIRECT_URI`)
      fehlen in Ihrer Umgebungsdatei.
    </ConfigurationError>,
  );
} else {
  const convex = new ConvexReactClient(convexUrl, { unsavedChangesWarning: false });

  /**
   * Keys ConvexProviderWithAuthKit on organizationId so that switching
   * organizations forces Convex to re-authenticate with a fresh token.
   * Without this, the Convex client keeps using the old org's token.
   */
  function ConvexWithOrgKey({ children }: { children: ReactNode }) {
    const { organizationId } = useAuth();

    return (
      <ConvexProviderWithAuthKit key={organizationId ?? 'no-org'} client={convex} useAuth={useAuth}>
        {children}
      </ConvexProviderWithAuthKit>
    );
  }

  createRoot(rootElement).render(
    <StrictMode>
      <PostHogProvider client={posthogClient}>
        <PostHogErrorBoundary fallback={<AppErrorFallback />}>
          <AuthKitProvider
            clientId={workosClientId}
            redirectUri={workosRedirectUri}
            apiHostname={workosApiHostname || undefined}
            devMode={workosDevMode}
          >
            <PostHogAuthBridge />
            <ConvexWithOrgKey>
              <BrowserRouter>
                <ConfirmProvider>
                  <App />
                </ConfirmProvider>
              </BrowserRouter>
            </ConvexWithOrgKey>
          </AuthKitProvider>
        </PostHogErrorBoundary>
      </PostHogProvider>
    </StrictMode>,
  );
}
